// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { func, string } from 'prop-types';
import { stringify, parse } from 'query-string';
import { useSWRConfig } from 'swr';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import download from 'Utils/download';
import useRouter from 'Hooks/useRouter';
import useProducts from 'Hooks/useProducts';
import ToolbarHeaderComponent from 'Components/Products/ToolbarHeader';
import { LATEST_PATH } from 'Contexts/ProductsContext';
import { UserContext } from 'Contexts/UserContext';
import useFilters from 'Hooks/useFilters';
import useScrollLock from 'Hooks/useScrollLock';
import { UIContext } from 'Contexts/UIContext';
import useAnalytics from 'Hooks/useAnalytics';

const defaultSortId = 'added-desc';
const queryFilters = [
  'only_promotional',
  'only_resealed',
  'eol',
  'only_available',
  'include_reserved_stock',
  'stock-clearing-sale'
];

const ToolbarHeader = memo(({ viewMode, setMobileFiltersOpen }) => {
  const [downloading, setDownloading] = useState(null);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const { t } = useTranslation();
  const { query, location, push } = useRouter();
  const { cache } = useSWRConfig();
  const setUiState = useContextSelector(UIContext, data => data.setUiState);
  const { data: { recordsPerPage } = {} } = useProducts();
  const language = useContextSelector(UserContext, ({ data }) => data.language);
  const analytics = useAnalytics();

  const { activeFilters = {} } = useFilters();
  const appliedFilters = Object.keys(omit(activeFilters, ['category'])).length + Object.entries(query).filter(([key]) => queryFilters.includes(key)).length;

  const sortOptions = useMemo(() => [
    { id: 'added-desc', label: t('products.sorting.newest') },
    { id: 'relevance-asc', label: t('products.sorting.relevance') },
    { id: 'price-asc', label: t('products.sorting.priceAsc') },
    { id: 'price-desc', label: t('products.sorting.priceDesc') },
    { id: 'percent_client_advantage-desc', label: t('products.sorting.promotions') },
    { id: 'total_ordered-desc', label: t('products.sorting.totalOrdered') }
  ], []);

  const perPageOptions = useMemo(() => [
    { id: 20, label: '20' },
    { id: 40, label: '40' },
    { id: 60, label: '60' }
  ], []);
  const defaultSort = sortOptions[0];
  const defaultPerPage = perPageOptions[0];
  const currentPerPageId = parseInt(query?.count || recordsPerPage, 10) || 20;
  const currentSortId = query?.order_by
    ? `${ query.order_by }-${ query.order_direction }`
    : defaultSortId;
  const [currentPerPage, setPerPage] = useState(
    currentPerPageId !== defaultPerPage.id
      ? perPageOptions.find(item => item.id === currentPerPageId)
      : defaultPerPage
  );
  const [currentSort, setSort] = useState(
    currentSortId !== defaultSort.id
      ? sortOptions.find(item => item.id === currentSortId)
      : defaultSort
  );

  useEffect(() => {
    /* eslint-disable camelcase */
    const { order_by, order_direction, count } = query;
    const newCount = parseInt(count, 10);
    if (!Number.isNaN(newCount) && newCount !== currentPerPage?.id) {
      analytics.track('change_products_per_page', {
        per_page: newCount || defaultPerPage.id,
        previous_per_page: currentPerPage?.id || defaultPerPage.id
      });
      setPerPage(
        count
          ? perPageOptions.find(item => item.id === newCount)
          : defaultPerPage
      );
    }
    const newSort = `${ order_by }-${ order_direction }`;
    if ((order_by && order_direction) && newSort !== currentSort?.id) {
      analytics.track('change_products_sorting', {
        sorting: newSort,
        previous_sorting: currentSort?.id || defaultSortId
      });
      setSort(
        order_by
          ? sortOptions.find(item => item.id === newSort)
          : defaultSort
      );
    }
    /* eslint-enable camelcase */
  }, [currentSort, currentPerPage, query]);

  useScrollLock(mobileSortOpen);

  const handleOnViewChange = mode => {
    analytics.track('change_products_view_mode', {
      view_mode: mode,
      previous_view_mode: viewMode
    });
    setUiState({ viewMode: mode });
    push({
      pathname: location.pathname,
      search: stringify({ ...query, ref: mode })
    });
  };

  const handleOnDownload = async type => {
    const path = cache.get(LATEST_PATH).replace(`/products`, '/products/export');

    // Remove page query param
    const [urlPath, urlQuery] = path.split('?');
    const queryParams = parse(urlQuery);
    const newQueryString = stringify(omit(queryParams, ['page']));
    const newPath = newQueryString ? `${urlPath}?${newQueryString}` : urlPath;

    try {
      setDownloading(type);
      await download(`${ newPath }&language=${ language }&format=${ type }`);
      setDownloading(null);
    } catch (err) {
      console.error('Could not download file', err.message);
    }
    return null;
  };

  /**
   * @type {function(*)}
   */
  const itemToElement = type => item => {
    const payload = {
      ...query
    };
    if (type === 'count') {
      if (item.id !== defaultPerPage.id) {
        payload.count = item.id;
        delete payload.page;
      } else {
        delete payload.count;
      }
    }
    if (type === 'order') {
      const [, orderBy, direction] = item.id?.match(/(\w+)-(\w+)/) || [];
      payload.order_by = orderBy;
      payload.order_direction = direction;
    }
    return (
      <Link
        to={ {
          ...location,
          search: stringify(payload)
        } }
      >
        { item.label }
      </Link>
    );
  };

  const handleMobileOrderChange = id => {
    const payload = {
      ...query
    };

    if (id !== defaultSort.id) {
      const [, orderBy, direction] = id?.match(/(\w+)-(\w+)/) || [];
      payload.order_by = orderBy;
      payload.order_direction = direction;
    } else {
      delete payload.order_by;
      delete payload.order_direction;
    }

    push({
      pathname: location.pathname,
      search: stringify(payload)
    });
  };

  return (
    <ToolbarHeaderComponent
      downloadingType={ downloading }
      onViewChange={ handleOnViewChange }
      onDownload={ handleOnDownload }
      viewMode={ viewMode }
      currentSort={ currentSort }
      itemToElement={ itemToElement }
      currentPerPage={ currentPerPage }
      sortOptions={ sortOptions }
      perPageOptions={ perPageOptions }
      setMobileFiltersOpen={ setMobileFiltersOpen }
      activeFilters={ appliedFilters }
      handleMobileOrderChange={ handleMobileOrderChange }
      mobileSortOpen={ mobileSortOpen }
      setMobileSortOpen={ setMobileSortOpen }
      recordsPerPage={ recordsPerPage }
    />
  );
});

ToolbarHeader.propTypes = {
  viewMode: string,
  setMobileFiltersOpen: func,
};

ToolbarHeader.displayName = 'ToolbarHeaderContainer';

export default ToolbarHeader;
